import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Input, Space, Typography, Progress, Tag, Divider, Row, Col } from 'antd';
import { SendOutlined, CheckCircleOutlined, SoundOutlined, FileTextOutlined, PictureOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createEmptyAILessonPromptingConfig, TAILessonPromptingConfig } from '../../../models/courses/aiLessonPrompting';
import './AILessonPromptingStudent.css';
import { useUserApi } from '../../../api/user/userApp';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface AILessonPromptingStudentProps {
  config: TAILessonPromptingConfig;
  statement?: string;
  onComplete?: () => void;
  forceReveal?: boolean;
  lessonId?: number;
}

// Get the progressive text index based on matched keywords
// matched = 1 → index 0, matched = 2 → index 1, etc.
const getProgressiveIndex = (matched: number, arrayLength: number): number => {
  if (matched <= 0 || arrayLength === 0) return -1;
  return Math.min(matched - 1, arrayLength - 1);
};

const stripPunctuation = (s: string) => s.replace(/[.,]/g, '');

const computeMatch = (prompt: string, keywords: string[]) => {
  if (!keywords.length) return { matched: 0, total: 0, found: [] as string[] };
  const normalizedPrompt = stripPunctuation(prompt).toLowerCase();
  const found = keywords.filter((kw) => normalizedPrompt.includes(stripPunctuation(kw).toLowerCase()));
  return { matched: found.length, total: keywords.length, found };
};

const clampRatio = (matched: number, required: number) => {
  if (required <= 0) return matched > 0 ? 1 : 0;
  return Math.min(1, matched / required);
};

const AILessonPromptingStudent: React.FC<AILessonPromptingStudentProps> = ({
  config,
  statement,
  onComplete,
  forceReveal = false,
  lessonId,
}) => {
  const { t } = useTranslation();
  const safeConfig = { ...createEmptyAILessonPromptingConfig(), ...config };
  const [prompt, setPrompt] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedPrompt, setSubmittedPrompt] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const noiseCtxRef = useRef<AudioContext | null>(null);
  const noiseSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const hasSavedRef = useRef(false);
  const completedRef = useRef(false);

  const effectivePrompt = useMemo(() => (forceReveal ? prompt : submittedPrompt), [forceReveal, prompt, submittedPrompt]);

  const { matched, total, found } = useMemo(
    () => computeMatch(effectivePrompt, safeConfig.keywords),
    [effectivePrompt, safeConfig.keywords],
  );

  const required = Math.min(safeConfig.requiredKeywords || 0, total);
  // For images, show with blur even if not submitted yet (max blur = 10px)
  // For text/audio, keep the submitted requirement
  const ratio = useMemo(() => {
    if (forceReveal) return 1;
    if (safeConfig.renderType === 'image') {
      // For images: if not submitted, show max blur (ratio = 0)
      // If submitted, calculate based on matched keywords
      return submitted ? clampRatio(matched, required || total || 1) : 0;
    }
    // Keep submit requirement for text/audio
    return submitted ? clampRatio(matched, required || total || 1) : 0;
  }, [forceReveal, matched, required, total, submitted, safeConfig.renderType]);

  const { getUserAnswerById, saveUserAnswerById } = useUserApi();

  // Load saved workspace
  useEffect(() => {
    // reset per-lesson guards
    hasSavedRef.current = false;
    completedRef.current = false;
    if (!lessonId) return;
    getUserAnswerById(lessonId)
      .then((resp: any) => {
        const workspace: string | undefined = resp?.data?.workspace;
        if (!workspace) return;
        try {
          const parsed = JSON.parse(workspace);
          if (parsed && typeof parsed === 'object') {
            if (typeof parsed.prompt === 'string') setPrompt(parsed.prompt);
            if (typeof parsed.submittedPrompt === 'string') {
              setSubmittedPrompt(parsed.submittedPrompt);
              setSubmitted(Boolean(parsed.submitted));
            }
          }
        } catch (_) {
          // ignore invalid workspace
        }
      })
      .catch(() => {/* ignore */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  useEffect(() => {
    if (ratio >= 1 && onComplete && !completedRef.current) {
      // Save user answer workspace once on success
      if (lessonId && !hasSavedRef.current) {
        const payload = {
          workspace: JSON.stringify({
            prompt,
            submittedPrompt,
            submitted: true,
            found,
          }),
        };
        try { saveUserAnswerById(lessonId, payload).catch(() => {/* ignore */}); } catch(_) {/* ignore */}
        hasSavedRef.current = true;
      }
      completedRef.current = true;
      onComplete();
    }
  }, [ratio, onComplete, lessonId, prompt, submittedPrompt, found, saveUserAnswerById]);

  useEffect(() => () => {
    if (noiseSourceRef.current) noiseSourceRef.current.stop();
    if (noiseCtxRef.current) noiseCtxRef.current.close();
  }, []);

  const startNoise = (level: number) => {
    if (level <= 0) return;
    const ctx = new AudioContext();
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate); // 1s buffer
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * level;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true; // loop to match audio length
    const gain = ctx.createGain();
    gain.gain.value = Math.min(1, level * 0.8);
    source.connect(gain).connect(ctx.destination);
    source.start();
    noiseCtxRef.current = ctx;
    noiseSourceRef.current = source;
    source.onended = () => {
      ctx.close();
      if (noiseSourceRef.current === source) {
        noiseSourceRef.current = null;
        noiseCtxRef.current = null;
      }
    };
  };

  const stopNoise = () => {
    if (noiseSourceRef.current) {
      try {
        noiseSourceRef.current.stop();
      } catch {
        // ignore
      }
    }
    if (noiseCtxRef.current) {
      noiseCtxRef.current.close();
    }
    noiseSourceRef.current = null;
    noiseCtxRef.current = null;
  };

  const handleAudioPlay = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration || 0);
    stopNoise();
    setIsPlaying(true);
    setHasPlayedOnce(true);
    startNoise(Math.max(0, 1 - ratio));
  };

  const handleAudioPauseOrEnd = () => {
    stopNoise();
    setIsPlaying(false);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    setSubmittedPrompt(prompt);
    // Save immediately regardless of correctness
    if (lessonId) {
      const payload = {
        workspace: JSON.stringify({
          prompt,
          submittedPrompt: prompt,
          submitted: true,
          // Save current found keywords snapshot too
          found,
        }),
      };
      try {
        saveUserAnswerById(lessonId, payload).catch(() => {/* ignore */});
      } catch (_) {
        // ignore
      }
    }
    // Autoplay audio synchronously on user gesture
    if (!forceReveal && safeConfig.renderType === 'audio' && safeConfig.answerAudio && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      } catch {
        // ignore autoplay rejection; onPlay fallback handles noise sync
      }
    }
  };


  const blurPx = Math.round((1 - ratio) * 10);

  // Get progressive text based on matched keywords
  const getDisplayText = (): string => {
    if (safeConfig.renderType !== 'text') return '';
    
    // Use answerTexts array if available (new format)
    if (safeConfig.answerTexts && safeConfig.answerTexts.length > 0) {
      const index = getProgressiveIndex(matched, safeConfig.answerTexts.length);
      if (index >= 0 && index < safeConfig.answerTexts.length) {
        return safeConfig.answerTexts[index];
      }
      return '';
    }
    
    // Fallback to old answerText format for backward compatibility
    if (safeConfig.answerText) {
      return safeConfig.answerText;
    }
    
    return t('aiPrompting.student.defaultAnswer') || '';
  };

  const displayText = getDisplayText();

  const getRenderTypeIcon = () => {
    switch (safeConfig.renderType) {
      case 'text': return <FileTextOutlined />;
      case 'image': return <PictureOutlined />;
      case 'audio': return <SoundOutlined />;
      default: return null;
    }
  };

  return (
    <div className="ai-prompting-student">
      {(statement) && (
        <Card className="ai-prompting-statement-card">
          <div className="ai-prompting-statement">{statement}</div>
        </Card>
      )}
      {safeConfig.renderType === 'audio' && safeConfig.answerAudio && (
        <audio
          ref={audioRef}
          src={safeConfig.answerAudio}
          onPlay={handleAudioPlay}
          onEnded={handleAudioPauseOrEnd}
          onPause={handleAudioPauseOrEnd}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
          controls={false}
          preload="auto"
          style={{ display: 'none' }}
        />
      )}

      <Card className="ai-prompting-main-card">
        {safeConfig.renderType === 'image' ? (
          <Row gutter={24} className="ai-prompting-split-layout">
            <Col xs={24} md={12} className="ai-prompting-prompt-col">
              <div className="ai-prompting-prompt-section">
                <div className="ai-prompting-section-header">
                  <Title level={5} style={{ margin: 0 }}>
                    {t('aiPrompting.student.promptLabel') || 'Rédige ta prompt'}
                  </Title>
                </div>
                <TextArea
                  rows={5}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={t('aiPrompting.student.promptPlaceholder') || 'Ajoute les mots-clés demandés...'}
                  className="ai-prompting-textarea"
                  disabled={forceReveal}
                />

                {!forceReveal && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<SendOutlined />}
                    onClick={handleSubmit}
                    disabled={!prompt.trim() || (submitted && prompt === submittedPrompt)}
                    className="ai-prompting-submit-btn"
                    block
                  >
                    {t('aiPrompting.student.send') || 'Envoyer'}
                  </Button>
                )}
              </div>
            </Col>
            <Col xs={24} md={12} className="ai-prompting-output-col">
              <div className="ai-prompting-output-section">
                <div className="ai-prompting-section-header">
                  <Title level={5} style={{ margin: 0 }}>
                    <Space>
                      {getRenderTypeIcon()}
                      {t('aiPrompting.student.output') || 'Rendu'}
                    </Space>
                  </Title>
                </div>

                <Card className="ai-prompting-output-card">
                  {safeConfig.answerImage && (
                    <div className="ai-prompting-image-container">
                      {/* Two images stacked vertically */}
                      <Space direction="vertical" size="large" style={{ width: '100%', alignItems: 'center' }}>
                        {/* Clear image (goal/target) - always clear */}
                        <div className="ai-prompting-image-wrapper">
                          <div className="ai-prompting-image-label">
                            <Text strong>{t('aiPrompting.student.goalImage') || 'Image cible'}</Text>
                          </div>
                          <img
                            src={safeConfig.answerImage}
                            alt="goal"
                            style={{ filter: 'blur(0px)' }} // Always clear
                          />
                        </div>
                        
                        {/* Blurry image (current) - updates on submit */}
                        <div className="ai-prompting-image-wrapper">
                          <div className="ai-prompting-image-label">
                            <Text strong>{t('aiPrompting.student.currentImage') || 'Votre rendu'}</Text>
                          </div>
                          <img
                            src={safeConfig.answerImage}
                            alt="current"
                            style={{ filter: `blur(${blurPx}px)` }} // Blur based on keywords
                          />
                          {ratio >= 1 && (
                            <div className="ai-prompting-image-overlay">
                              <Tag color="success" icon={<CheckCircleOutlined />}>
                                {t('aiPrompting.student.imageCompleteRevealed') || 'Image complète révélée'}
                              </Tag>
                            </div>
                          )}
                        </div>
                      </Space>
                    </div>
                  )}
                </Card>
              </div>
            </Col>
          </Row>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div className="ai-prompting-prompt-section">
              <div className="ai-prompting-section-header">
                <Title level={5} style={{ margin: 0 }}>
                  {t('aiPrompting.student.promptLabel') || 'Rédige ta prompt'}
                </Title>
              </div>
              <TextArea
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('aiPrompting.student.promptPlaceholder') || 'Ajoute les mots-clés demandés...'}
                className="ai-prompting-textarea"
                disabled={forceReveal}
              />

              {!forceReveal && (
                <Button
                  type="primary"
                  size="large"
                  icon={<SendOutlined />}
                  onClick={handleSubmit}
                  disabled={!prompt.trim() || (submitted && prompt === submittedPrompt)}
                  className="ai-prompting-submit-btn"
                  block
                >
                  {t('aiPrompting.student.send') || 'Envoyer'}
                </Button>
              )}
            </div>

            <Divider />

            <div className="ai-prompting-output-section">
              <div className="ai-prompting-section-header">
                <Title level={5} style={{ margin: 0 }}>
                  <Space>
                    {getRenderTypeIcon()}
                    {t('aiPrompting.student.output') || 'Rendu'}
                  </Space>
                </Title>
              </div>

              {!forceReveal && !submitted ? (
                <Card className="ai-prompting-placeholder-card">
                  <Alert
                    type="info"
                    message={t('aiPrompting.student.submitToReveal') ||
                      "Le rendu s'affichera après avoir cliqué sur Envoyer."}
                    showIcon
                  />
                </Card>
              ) : (
                <Card className="ai-prompting-output-card">
                  {safeConfig.renderType === 'text' && (
                    <div className="ai-prompting-text-container">
                      {displayText ? (
                        <>
                          <Paragraph className="ai-prompting-text">{displayText}</Paragraph>
                          {matched >= required && (
                            <Tag color="success" icon={<CheckCircleOutlined />} className="ai-prompting-success-tag">
                              {t('aiPrompting.student.textCompleteRevealed') || 'Texte complet révélé'}
                            </Tag>
                          )}
                        </>
                      ) : (
                        <Alert
                          type="warning"
                          message={t('aiPrompting.student.tryAgain') || 'Aucun mot-clé trouvé. Réessayez avec les bons mots-clés.'}
                          showIcon
                        />
                      )}
                    </div>
                  )}
                  {safeConfig.renderType === 'audio' && safeConfig.answerAudio && (
                    <div className="ai-prompting-audio-container">
                      <div className="ai-prompting-audio">
                        <div className="ai-prompting-audio-progress">
                          <Progress 
                            percent={duration > 0 ? Math.round((currentTime / duration) * 100) : 0} 
                            showInfo={false}
                            strokeColor={ratio >= 1 ? '#52c41a' : '#1890ff'}
                          />
                        </div>
                        <div className="ai-prompting-audio-controls">
                          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                            <div className="ai-prompting-audio-info">
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {t('aiPrompting.student.noiseLevel', { level: Math.round((1 - ratio) * 100) }) ||
                                  `Bruit: ${Math.round((1 - ratio) * 100)}%`}
                              </Text>
                              {ratio >= 1 && (
                                <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginLeft: 8 }}>
                                  Audio clair
                                </Tag>
                              )}
                            </div>
                            {(!isPlaying && hasPlayedOnce) && (
                              <Button 
                                type="primary" 
                                icon={<SoundOutlined />}
                                onClick={() => { 
                                  if (audioRef.current) { 
                                    audioRef.current.currentTime = 0; 
                                    audioRef.current.play(); 
                                  } 
                                }}
                              >
                                {t('aiPrompting.student.replay') || 'Relancer'}
                              </Button>
                            )}
                            {(!isPlaying && !hasPlayedOnce && forceReveal) && (
                              <Button 
                                type="primary" 
                                icon={<SoundOutlined />}
                                onClick={() => { 
                                  if (audioRef.current) { 
                                    audioRef.current.currentTime = 0; 
                                    audioRef.current.play(); 
                                  } 
                                }}
                              >
                                {t('aiPrompting.student.play') || 'Écouter'}
                              </Button>
                            )}
                          </Space>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </div>
          </Space>
        )}
        
        {!forceReveal && submitted && ratio < 1 && (
          <Alert
            type="warning"
            showIcon
            message={t('aiPrompting.student.improvePrompt') || 'Améliore ta prompt pour afficher un rendu correct.'}
            className="ai-prompting-improvement-alert"
            style={{ marginTop: 24 }}
          />
        )}
        
        {!forceReveal && submitted && ratio >= 1 && (
          <Alert
            type="success"
            showIcon
            message="Excellent ! Tu as trouvé tous les mots-clés requis."
            className="ai-prompting-success-alert"
            style={{ marginTop: 24 }}
          />
        )}
      </Card>
    </div>
  );
};

export default AILessonPromptingStudent;
